import axios from "axios";
import * as Analytics from "./Analytics";
import {SessionContext} from "./SessionContext";

// A 401 THAT MEANS "THAT PASSWORD IS WRONG" IS NOT AN EXPIRED SESSION, and must not be replayed.
//
// The handler below refreshes the token and retries on every 401. For a re-authentication refusal
// that retry submits the same wrong password a second time, and each attempt counts against the
// account's five-strike lockout -- so one click costs two strikes and a person gets two and a half
// tries before they are locked out for an hour. Measured in prod: one Record click left the
// counter at 2.
//
// A DENY LIST, NOT AN ALLOW LIST. An unknown 401 still refreshes and replays exactly as before, so
// session renewal cannot regress on a code nobody thought of. These four are the ones that mean
// the credential just typed was wrong, missing, or already locked out.
const REAUTHENTICATION_REFUSALS = ["CC-OAPR-PWBD", "CC-OAPR-PWMS", "CC-OAPR-PWNO", "CC-OAPR-PWLK"];

function isAReauthenticationRefusal(response: any): boolean {
    const code = response?.data?.Code;
    return typeof code === "string" && REAUTHENTICATION_REFUSALS.includes(code);
}

export function makeCresAxios(sessionContext: any) {

    // maybe a dict, null or a SessionContext object depending on login completion status
    if (!sessionContext) sessionContext = new SessionContext();

    const instance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        // metadata: {},
    });

    instance.interceptors.request.use(
        (config: any) => {

            if (config.metadata === undefined) {
                config.metadata = {};
                config.metadata.startTime = null;
                config.metadata.endTime = null;
            }
            config.metadata.startTime = new Date();

            if (config.url !== "t1/v1/log") {
                Analytics.record("API Request", {
                    "HTTP URL": config.url,
                    "HTTP Method": config.method,
                });
            }

           const token = sessionContext.getAccessToken();
            if (token) config.headers["X-Cres-AccessToken"] = token;

            // THE ACTIVE PERSONA TRAVELS WITH EVERY REQUEST, for the reason AxiosFoundationAPI
            // gives at length: the server scopes a read by the persona these two headers name, and
            // a caller that has to remember them is a caller that will forget.
            //
            // THIS INSTANCE WAS THE ONE THAT FORGOT. The Foundation instance grew the block below
            // on 2026-09-16; this one never did, and t1's dispatcher now requires a persona on
            // every door that does not explicitly opt out. So each of the eight t1 endpoints
            // reached through here -- the Form tab's definition, input and promotion, and the whole
            // Form Designer page -- was refused CC-PRSN-NONE at 400 the moment that default landed.
            // The tab showed "This record's answers could not be loaded.", because the refusal body
            // carries a Code and no Cause in prod and the component falls back to its own sentence.
            // Found on SOP-00197, 16 October 2026.
            //
            // ONLY WHEN NOT ALREADY SET, and null and empty count as unset: an enterprise component
            // querying under a scope the page chose has made a decision, and this fills gaps rather
            // than overriding it.
            const active = sessionContext.getActiveVisibility();
            const requested = config.headers["visibility-mode"];
            if (active?.Mode && (requested === undefined || requested === null || requested === "")) {
                config.headers["visibility-mode"] = active.Mode;
                config.headers["visibility-value"] = active.Value ?? "";
            }

            return config;
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
        (response: any) => {

            // Sometime response.data is string instead of object #1723
            // https://github.com/axios/axios/issues/1723
            if (response.headers["content-type"]?.includes("application/json")) {
                if (typeof response.data === "string" && response.data !== "") {
                    try {
                        response.data = JSON.parse(
                            response.data
                                .replace(/\\/g, "\\\\")
                                .replace(/\n/g, "\\n")
                                .replace(/\r/g, "\\r")
                                .replace(/\\"/g, "\""),
                        );
                    } catch (e) {
                        console.error(e);
                    }
                }
            }

            const config = response.config;

            config.metadata.endTime = new Date();

            if (config.url !== "t1/v1/log") {
                Analytics.record("API Response", {
                    "HTTP URL": config.url,
                    "HTTP Method": config.method,
                    "HTTP Code": response.status,
                    "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                    "Reason Code": response.data ? response.data.Code : undefined,
                });
            }

            return response;
        },
        (error) => {

            const config = error.config;
            const response = error.response;

            config.metadata.endTime = new Date();

            // response can be undefined when there is a network issue / no connection
            if (response === undefined) {
                return Promise.reject(error);
            }

            if (config.url !== "t1/v1/log") {
                Analytics.record("API Response", {
                    "HTTP URL": config.url,
                    "HTTP Method": config.method,
                    "HTTP Code": response.status,
                    "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                    "Reason Code": response.data ? response.data.Code : undefined,
                });
            }

            if (config.metadata.disableRefresh !== true) {
                if (config.metadata.cogntioRefreshed === undefined
                    && !isAReauthenticationRefusal(response)) {

                    config.metadata.cogntioRefreshed = true;

                    if (response.status === 401) {
                        return axios({
                            method: "PUT",
                            url: `${process.env.REACT_APP_API_URL}/t1/v1/authenticate`,
                            data: {
                                "X-Cres-RefreshToken": sessionContext.getRefreshToken(),
                            },
                        }).then((response) => {
                            const token = response.data.AccessToken;
                            sessionContext.setAccessToken(token);
                            sessionContext.setS3AccessKeyId(response.data["S3AccessKeyId"])
                            sessionContext.setS3SecretAccessKey(response.data["S3SecretAccessKey"])
                            sessionContext.setS3SessionToken(response.data["S3SessionToken"])
                            config.headers["X-Parkour-AccessToken"] = token;
                            // Retry through `instance`, NOT raw axios: the response interceptor must run on the retry
                            // too, or its JSON-string repair is skipped and callers silently receive an unparsed body.
                            // No loop risk: cogntioRefreshed is already set, so a second 401 falls through to reject.
                            return instance(config);
                        });
                    }
                }
            }

            return Promise.reject(error);
        },
    );

    return instance;
}

const Axios = makeCresAxios(null);

export default Axios;
